"use client"

import React, { useState, useEffect, useCallback } from "react"
import { useParams } from "react-router-dom"
import { Card, Flex, Text } from "@radix-ui/themes"
import { ConciergeBell, Clock, CheckCircle, RotateCcw, RefreshCw, AlertTriangle, Package, User, Hash, Phone } from "lucide-react"
import { useLanguage } from "../../contexts/LanguageContext"
import { useRestaurantAuth } from "../../contexts/RestaurantAuthContext"
import { useToast } from "../../hooks/use-toast"
import { Button } from "../../components/ui/button"
import { Label } from "../../components/ui/label"
import PageHeader from "../../components/ui/dashboard/PageHeader"
import StatCard from "../../components/ui/dashboard/StatCard"
import LoadingSpinner from "../../components/ui/dashboard/LoadingSpinner"
import { restaurantPortalAPI } from "../../services/restaurantApiClient"

interface OrderItem {
    foodItem: {
        _id: string
        name: string
        nameAr?: string
    }
    quantity: number
    price: number
    size?: {
        name: string
        nameAr?: string
        price: number
    }
    specialInstructions?: string
}

interface Order {
    _id: string
    orderNumber: string
    status: string
    orderType: string
    items: OrderItem[]
    table?: {
        _id: string
        tableNumber: string
    }
    customerInfo?: {
        name: string
        phone: string
    }
    createdAt: string
    pricing: {
        total: number
    }
}

const WaiterOrdersPage: React.FC = () => {
    const { restaurantId } = useParams()
    const { t, language, isRTL, dir } = useLanguage()
    const { user } = useRestaurantAuth()
    const { toast } = useToast()

    const [orders, setOrders] = useState<Order[]>([])
    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [updatingOrder, setUpdatingOrder] = useState<string | null>(null)
    const [returnModalOpen, setReturnModalOpen] = useState(false)
    const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
    const [returnReason, setReturnReason] = useState("")

    const fetchOrders = useCallback(async () => {
        if (!restaurantId) return

        try {
            const response = await restaurantPortalAPI.getOrders(restaurantId, {
                status: "ready",
                limit: 50
            })
            // Orders are already filtered by the API
            setOrders(response.data.orders)
        } catch (error) {
            console.error("Error fetching orders:", error)
            toast({
                title: t("error"),
                description: t("failedToLoadOrders"),
                variant: "destructive",
            })
        } finally {
            setLoading(false)
            setRefreshing(false)
        }
    }, [restaurantId, t, toast])

    useEffect(() => {
        fetchOrders()
        // Auto-refresh every 30 seconds
        const interval = setInterval(fetchOrders, 30000)
        return () => clearInterval(interval)
    }, [fetchOrders])

    const handleRefresh = () => {
        setRefreshing(true)
        fetchOrders()
    }

    const updateOrderStatus = async (orderId: string, newStatus: string, reason?: string) => {
        setUpdatingOrder(orderId)
        try {
            const updateData: any = { status: newStatus }
            if (reason) {
                updateData.returnReason = reason
                updateData.returnedBy = user?._id
            }

            await restaurantPortalAPI.updateOrderStatus(orderId, updateData)
            toast({
                title: t("success"),
                description: newStatus === "returned" ? t("orderReturned") : t("orderServed"),
            })
            fetchOrders()
            setReturnModalOpen(false)
            setSelectedOrder(null)
            setReturnReason("")
        } catch (error) {
            console.error("Error updating order:", error)
            toast({
                title: t("error"),
                description: t("failedToUpdateStatus"),
                variant: "destructive",
            })
        } finally {
            setUpdatingOrder(null)
        }
    }

    const handleServe = (orderId: string) => {
        updateOrderStatus(orderId, "served")
    }

    const handleReturnClick = (order: Order) => {
        setSelectedOrder(order)
        setReturnModalOpen(true)
    }

    const handleReturnSubmit = () => {
        if (!selectedOrder || !returnReason.trim()) {
            toast({
                title: t("error"),
                description: t("pleaseProvideReturnReason"),
                variant: "destructive",
            })
            return
        }
        updateOrderStatus(selectedOrder._id, "returned", returnReason)
    }

    const getTimeSinceReady = (createdAt: string) => {
        const minutes = Math.floor((Date.now() - new Date(createdAt).getTime()) / 60000)
        if (minutes < 60) return `${minutes} ${t("minutesAgo")}`
        const hours = Math.floor(minutes / 60)
        return `${hours} ${t("hoursAgo")}`
    }

    const formatTime = (dateString: string) => {
        return new Date(dateString).toLocaleTimeString(language === 'ar' ? 'ar-SA' : 'en-US', {
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    if (loading) {
        return <LoadingSpinner size="3" text={t("loadingOrders")} />
    }

    return (
        <div className="space-y-6" dir={dir}>
            {/* Header */}
            <PageHeader
                title={t("waiterOrders")}
                subtitle={t("waiterOrdersDescription")}
            >
                <Button
                    onClick={handleRefresh}
                    variant="outline"
                    disabled={refreshing}
                    className="flex items-center gap-2"
                >
                    <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                    {t("refresh")}
                </Button>
            </PageHeader>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <StatCard
                    title={t("readyToServe")}
                    value={orders.length}
                    icon={ConciergeBell}
                    color="bg-green-500"
                />
                <StatCard
                    title={t("totalOrders")}
                    value={orders.length}
                    icon={Package}
                    color="bg-blue-500"
                />
                <StatCard
                    title={t("avgPrepTime")}
                    value="5 min"
                    icon={Clock}
                    color="bg-purple-500"
                />
            </div>

            {/* Orders Grid */}
            {orders.length === 0 ? (
                <Card>
                    <Flex direction="column" align="center" justify="center" p="9" gap="3">
                        <ConciergeBell size={48} style={{ color: "var(--gray-9)" }} />
                        <Text size="5" weight="medium" className="text-gray-500 dark:text-gray-400">
                            {t("noReadyOrders")}
                        </Text>
                        <Text size="2" color="gray">
                            {t("readyOrdersWillAppear")}
                        </Text>
                    </Flex>
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {orders.map((order) => (
                        <Card key={order._id} className="overflow-hidden border-l-4 border-l-green-500">
                            {/* Order Header */}
                            <div className="px-4 py-3 bg-green-50 dark:bg-green-900/20">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Hash className="w-4 h-4 text-gray-500" />
                                        <span className="text-lg font-bold text-gray-900 dark:text-white">
                                            {order.orderNumber}
                                        </span>
                                    </div>
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
                                        <CheckCircle className="w-3 h-3 mr-1" />
                                        {t("ready")}
                                    </span>
                                </div>
                                <div className="flex items-center gap-4 mt-2 text-sm text-gray-600 dark:text-gray-400">
                                    <div className="flex items-center gap-1">
                                        <Clock className="w-3.5 h-3.5" />
                                        <span>{formatTime(order.createdAt)}</span>
                                    </div>
                                    <span className="text-gray-400">•</span>
                                    <span>{getTimeSinceReady(order.createdAt)}</span>
                                    {order.table && (
                                        <>
                                            <span className="text-gray-400">•</span>
                                            <span className="font-medium text-green-700 dark:text-green-400">
                                                {t("table")} {order.table.tableNumber}
                                            </span>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Order Content */}
                            <div className="p-4">
                                {/* Customer Info */}
                                {order.customerInfo && (
                                    <div className="mb-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                        <div className="flex items-center gap-2 text-sm">
                                            <User className="w-4 h-4 text-gray-500" />
                                            <span className="font-medium text-gray-900 dark:text-white">
                                                {order.customerInfo.name}
                                            </span>
                                        </div>
                                        {order.customerInfo.phone && (
                                            <div className="flex items-center gap-2 text-sm mt-1 text-gray-600 dark:text-gray-400">
                                                <Phone className="w-4 h-4 text-gray-400" />
                                                <span>{order.customerInfo.phone}</span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Order Summary */}
                                <div className="mb-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            {order.items.length} {t("items")}
                                        </span>
                                        <span className="text-sm font-bold text-gray-900 dark:text-white">SDG {order.pricing.total.toFixed(2)}
                                        </span>
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                                        {order.items.slice(0, 3).map((item, i) => {
                                            const itemName = language === 'ar' && item.foodItem.nameAr
                                                ? item.foodItem.nameAr
                                                : item.foodItem.name
                                            const sizeName = item.size
                                                ? (language === 'ar' && item.size.nameAr
                                                    ? item.size.nameAr
                                                    : item.size.name)
                                                : ''
                                            return (
                                                <div key={i} className="flex items-center gap-1">
                                                    <span className="font-medium">{item.quantity}x</span>
                                                    <span className="truncate">{itemName}</span>
                                                    {sizeName && <span className="text-red-600">({sizeName})</span>}
                                                </div>
                                            )
                                        })}
                                        {order.items.length > 3 && (
                                            <div className="text-gray-400">
                                                +{order.items.length - 3} {t("more")}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleServe(order._id)}
                                        disabled={updatingOrder === order._id}
                                        className="flex-1 h-10 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                                    >
                                        <CheckCircle className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                                        {updatingOrder === order._id ? t("updating") : t("markServed")}
                                    </button>
                                    <Button
                                        onClick={() => handleReturnClick(order)}
                                        disabled={updatingOrder === order._id}
                                        variant="outline"
                                        className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20"
                                    >
                                        <RotateCcw className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            {/* Return Modal */}
            {returnModalOpen && selectedOrder && (
                <>
                    <div
                        className="fixed inset-0 bg-black/50 z-40"
                        onClick={() => {
                            setReturnModalOpen(false)
                            setSelectedOrder(null)
                            setReturnReason("")
                        }}
                    />
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <Card className="w-full max-w-md p-6 bg-white dark:bg-gray-800">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full">
                                    <AlertTriangle className="w-6 h-6 text-red-600" />
                                </div>
                                <div>
                                    <Text size="4" weight="bold" className="text-gray-900 dark:text-white">
                                        {t("returnOrder")}
                                    </Text>
                                    <Text size="2" className="text-gray-500 dark:text-gray-400">
                                        #{selectedOrder.orderNumber}
                                    </Text>
                                </div>
                            </div>

                            <div className="mb-4">
                                <Label className="text-gray-700 dark:text-gray-300">{t("returnReason")}</Label>
                                <textarea
                                    value={returnReason}
                                    onChange={(e) => setReturnReason(e.target.value)}
                                    placeholder={t("enterReturnReason")}
                                    className="w-full mt-2 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                    rows={4}
                                />
                            </div>

                            <div className="flex gap-3">
                                <Button
                                    onClick={() => {
                                        setReturnModalOpen(false)
                                        setSelectedOrder(null)
                                        setReturnReason("")
                                    }}
                                    variant="outline"
                                    className="flex-1"
                                >
                                    {t("cancel")}
                                </Button>
                                <Button
                                    onClick={handleReturnSubmit}
                                    disabled={!returnReason.trim() || updatingOrder === selectedOrder._id}
                                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                                >
                                    {updatingOrder === selectedOrder._id ? t("updating") : t("confirmReturn")}
                                </Button>
                            </div>
                        </Card>
                    </div>
                </>
            )}
        </div>
    )
}

export default WaiterOrdersPage
